import { memo, useEffect, useState } from 'react';
import { noop } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useElapsedTimeLabel } from './hooks/useElapsedTimeLabel';
import {
  EMPTY_MAP_FEATURES,
  LIVE_POSITION_ICON_BASE_ROTATION_DEG,
  LIVE_POSITION_ICON_SIZE_PX,
  LIVE_POSITION_ICON_SRC,
} from './utils/constants';
import {
  MapViewCanvas,
  MapViewCleaningAreaOverlayWrapper,
  MapViewContainer,
  MapViewFrame,
  MapViewImage,
  MapViewLivePositionIcon,
  MapViewLivePositionMarker,
  MapViewOverlaySvg,
  MapViewPlaceholder,
  MapViewPlannedPathPolyline,
  MapViewStatusBadge,
  MapViewTrailPolyline,
} from './MapView.styles';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import {
  worldHeadingToScreenDegrees,
  worldPointsToSvgPolylinePoints,
  worldToPercent,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/utils/mapCoordinates';
import type {
  LivePosition,
  MapInfo,
  PlannedPathPoint,
  SubscriptionStatus,
  TrailPoint,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/types/LiveMap.types';
// eslint-disable-next-line max-len
import type { RobotModelForLiveMap } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/liveMapActions.types';
// eslint-disable-next-line max-len
import { CleaningAreaMapOverlay } from 'app/modules/machine-inventory/machine-details-panel/components/MachineDetailsRobotPanel/MachineDetailsRobotSchedule/components/ScheduleCreateModal/components/CleaningScheduleCard/components/CleaningZones/CleaningAreaMapOverlay/CleaningAreaMapOverlay';
import type {
  MapArea,
  MapFeatures,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/components/MachineDetailsRobotPanel/MachineDetailsRobotSchedule/types/MapArea.types';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import type { Optional } from 'lib/types/Optional';

export interface MapViewProps {
  model: RobotModelForLiveMap;
  mapName: string;
  mapImageUrl: Optional<string>;
  /** `resolution`/`origin`/`verticalFlip` only — image pixel dimensions are resolved below. */
  mapInfo: Optional<Pick<MapInfo, 'resolution' | 'origin' | 'verticalFlip'>>;
  /** Real pixel dimensions for CV50 (from the Map entity); ignored for B50 (see onLoad below). */
  mapImageDimensionsPx: Optional<{ width: number; height: number }>;
  plannedPath: PlannedPathPoint[];
  trail: TrailPoint[];
  livePosition: Optional<LivePosition>;
  subscriptionStatus: SubscriptionStatus;
  cleaningZones: MapArea[];
  cleaningZoneFeatures: Optional<MapFeatures>;
  /** Zones the running task covers (AREA cleaning); null for any other scope. */
  activeCleaningZoneIds: Optional<string[]>;
  /** True while the map data is being fetched, so it isn't shown as unavailable / unknown yet. */
  isLoading: boolean;
}

const MapViewComponent = ({
  model,
  mapName,
  mapImageUrl,
  mapInfo,
  mapImageDimensionsPx,
  plannedPath,
  trail,
  livePosition,
  subscriptionStatus,
  cleaningZones,
  cleaningZoneFeatures,
  activeCleaningZoneIds,
  isLoading,
}: MapViewProps): JSX.Element => {
  const { t } = useTranslation();
  const { label: elapsedLabel, isStale } = useElapsedTimeLabel(livePosition?.lastUpdated);

  // B50's YAML has no width/height — the loaded <img>'s natural size is the source of truth.
  const [loadedImageSize, setLoadedImageSize] = useState<{ width: number; height: number } | null>(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  // Reset on a new image URL so a previous failure or the previous route's size doesn't stick around.
  useEffect(() => {
    setImageLoadFailed(false);
    setLoadedImageSize(null);
  }, [mapImageUrl]);

  // A B50 map image can take seconds to download; until it loads it is still loading, not unavailable.
  const isImageLoading = model === 'B50' && !!mapImageUrl && !imageLoadFailed && !loadedImageSize;
  const isMapLoading = isLoading || isImageLoading;
  const effectiveImageSize = model === 'B50' ? loadedImageSize : mapImageDimensionsPx ?? null;
  const effectiveMapInfo: MapInfo | null =
    mapInfo && effectiveImageSize
      ? { ...mapInfo, imageWidthPx: effectiveImageSize.width, imageHeightPx: effectiveImageSize.height }
      : null;

  const isDisconnected = !isLoading && (subscriptionStatus === 'disconnected' || isStale);
  const showPlaceholder = isLoading || !mapImageUrl || imageLoadFailed || !effectiveMapInfo;
  const aspectRatio = effectiveImageSize ? effectiveImageSize.width / effectiveImageSize.height : undefined;
  const livePositionPercent = effectiveMapInfo && livePosition ? worldToPercent(livePosition, effectiveMapInfo) : null;
  const livePositionRotationDeg =
    effectiveMapInfo && livePosition?.heading != null
      ? worldHeadingToScreenDegrees(livePosition.heading, effectiveMapInfo) -
        LIVE_POSITION_ICON_BASE_ROTATION_DEG[model]
      : 0;

  return (
    <MapViewContainer>
      <MapViewStatusBadge $isStale={isDisconnected} role="status">
        {isDisconnected && <SvgIcon name="warning" size={14} />}
        <span className="map-view__status-badge-count">
          {isLoading ? <PendingSpinnerIcon style={{ marginRight: '4px' }} /> : elapsedLabel}
        </span>
        {t('liveMap.lastLocationStatus.label')}
      </MapViewStatusBadge>

      <MapViewFrame>
        <MapViewCanvas $aspectRatio={aspectRatio}>
          {model === 'CV50' && mapImageUrl ? (
            <MapViewCleaningAreaOverlayWrapper>
              <CleaningAreaMapOverlay
                mapImageSrc={mapImageUrl}
                mapName={mapName}
                mapWidthPx={mapImageDimensionsPx?.width}
                mapHeightPx={mapImageDimensionsPx?.height}
                areas={cleaningZones}
                features={cleaningZoneFeatures ?? EMPTY_MAP_FEATURES}
                selectedAreaIds={activeCleaningZoneIds ?? []}
                onToggleArea={noop}
                onImageError={(): void => setImageLoadFailed(true)}
                hideStartMarkerRobot
                // AREA cleaning styles selected/unselected zones like the schedule create modal.
                areaStyle={activeCleaningZoneIds ? 'default' : 'wholeMap'}
                disabled
              />
            </MapViewCleaningAreaOverlayWrapper>
          ) : (
            mapImageUrl && (
              <MapViewImage
                src={mapImageUrl}
                alt={mapName}
                draggable={false}
                onLoad={(event): void => {
                  setImageLoadFailed(false);
                  setLoadedImageSize({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  });
                }}
                onError={(): void => setImageLoadFailed(true)}
              />
            )
          )}

          {effectiveMapInfo && (
            <MapViewOverlaySvg viewBox="0 0 100 100" preserveAspectRatio="none">
              <MapViewPlannedPathPolyline points={worldPointsToSvgPolylinePoints(plannedPath, effectiveMapInfo)} />
              <MapViewTrailPolyline points={worldPointsToSvgPolylinePoints(trail, effectiveMapInfo)} />
            </MapViewOverlaySvg>
          )}

          {livePositionPercent && (
            <MapViewLivePositionMarker
              aria-label={t('liveMap.robotPosition.label') as string}
              $xPercent={livePositionPercent.xPercent}
              $yPercent={livePositionPercent.yPercent}
            >
              <MapViewLivePositionIcon
                src={LIVE_POSITION_ICON_SRC[model]}
                alt=""
                draggable={false}
                $rotationDeg={livePositionRotationDeg}
                $sizePx={LIVE_POSITION_ICON_SIZE_PX[model]}
              />
            </MapViewLivePositionMarker>
          )}
        </MapViewCanvas>

        {showPlaceholder && (
          <MapViewPlaceholder>
            {isMapLoading ? <PendingSpinnerIcon /> : <SvgIcon name="nonIot" size={32} />}
            <span>{t(isMapLoading ? 'liveMap.mapLoading' : 'liveMap.mapUnavailable')}</span>
          </MapViewPlaceholder>
        )}
      </MapViewFrame>
    </MapViewContainer>
  );
};

// Its props are stable between the screen's re-renders (a clock tick, telemetry), so the map is not redrawn for them.
export const MapView = memo(MapViewComponent);
